import { useEffect, useState } from 'react';
import type { OrderDto, RefundMethod } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { humanize, money } from '@/lib/format';
import { paidByHand } from '@/lib/payment-state';
import { halalasToRiyals } from '@/lib/price-input';
import { checkRefundAmount } from '@/lib/returns';
import { Choices, ToggleField } from '@/components/fields';
import { Sheet } from '@/components/page';
import { Alert, Button, ConfirmDialog, Field, Input, Select, Textarea } from '@/components/ui';

/**
 * Money back on an order with no return behind it (ADR-061) — a goodwill
 * gesture, shipping on a late parcel, a refund made in a provider's dashboard.
 * The owner's alone.
 *
 * Built like a return's refund: it opens on what is left, says in words which
 * way the money goes before anything moves, and asks once more. Money that
 * came in by hand goes back by hand and says how; the whole of what is left
 * may put the lines back on sale, never on its own.
 */

/** A payment money was taken on — what a refund can go back through. */
const REFUNDABLE = ['SUCCEEDED', 'PARTIALLY_REFUNDED'];

const REFUND_METHODS: RefundMethod[] = ['BANK_TRANSFER', 'CASH', 'OTHER'];

/** The store's own balance share of an order (ADR-081): it goes back as credit, never to a card. */
const WALLET = 'wallet';

/** What is left on these payments to give back. */
function leftOn(payments: { amount: number; refundedAmount: number; status: string }[]): number {
  return payments.filter((row) => REFUNDABLE.includes(row.status)).reduce((sum, row) => sum + row.amount - row.refundedAmount, 0);
}

/** What is left to refund on the order, or null when the person may not refund it. */
export function refundableLeft(order: OrderDto, role: string | undefined): number | null {
  const payment = order.payment ?? null;
  if (role !== 'OWNER' || !payment) return null;
  // To store credit, every payment counts — the balance share as well as the card (ADR-081).
  const left = order.customer?.id ? leftOn(order.payments ?? [payment]) : REFUNDABLE.includes(payment.status) ? payment.amount - payment.refundedAmount : 0;
  return left > 0 ? left : null;
}

export function RefundSheet({ order, open, onClose }: { order: OrderDto; open: boolean; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { refund, updating, currentError, clearError } = useOrdersStore();

  const payment = order.payment ?? null;
  // A customer with an account may be refunded to store credit; an order the balance paid alone, only so (ADR-081).
  const creditable = Boolean(order.customer?.id);
  // Credit when nothing else can be refunded yet — cash not collected beside a balance share.
  const creditOnly = payment?.provider === WALLET || (creditable && (!payment || !REFUNDABLE.includes(payment.status)));
  const [toCredit, setToCredit] = useState(creditOnly);
  const credit = creditOnly || (creditable && toCredit);
  const left = credit
    ? leftOn(order.payments ?? (payment ? [payment] : []))
    : payment && REFUNDABLE.includes(payment.status)
      ? Math.max(0, payment.amount - payment.refundedAmount)
      : 0;
  const byHand = paidByHand(order) && !credit;
  const currency = order.totals.currency;
  const fmt = (halalas: number) => money(halalas, locale, currency);

  const [typed, setTyped] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState<RefundMethod | ''>('');
  const [reference, setReference] = useState('');
  const [restock, setRestock] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    clearError();
    setTyped(null);
    setReason('');
    setMethod('');
    setReference('');
    setRestock(false);
    setConfirming(false);
    setToCredit(creditOnly);
  }, [open, clearError, creditOnly]);

  // Untouched, the field reads what is left — nearly always the answer.
  const value = typed ?? halalasToRiyals(left);
  const check = checkRefundAmount(value, left);
  const amount = check.ok ? check.amount : null;
  const whole = amount !== null && amount >= left;
  const ready = amount !== null && reason.trim() !== '' && (!byHand || method !== '');

  const shown = amount === null ? '' : fmt(amount);
  const statement = credit
    ? t('refundToCredit', { amount: shown })
    : order.paymentMethod === 'MANUAL'
      ? t('refundToHand', { amount: shown })
      : byHand
        ? t('refundToCod', { amount: shown })
        : t('refundToCard', { amount: shown, provider: humanize(payment?.provider ?? '') });

  const amountError = check.ok
    ? undefined
    : check.error === 'over'
      ? t('refundAmountOver', { max: fmt(left) })
      : check.error === 'zero'
        ? t('refundAmountZero')
        : t('refundAmountInvalid');

  async function confirm() {
    if (!ready || amount === null) return;
    const ok = await refund(order.id, {
      amount,
      reason: reason.trim(),
      ...(credit
        ? { settlement: { method: 'STORE_CREDIT' as const } }
        : byHand && method
          ? { settlement: { method, ...(reference.trim() ? { reference: reference.trim() } : {}) } }
          : {}),
      ...(whole && restock ? { restock: true } : {}),
    });
    setConfirming(false);
    if (ok) onClose();
  }

  return (
    <>
      <Sheet
        open={open}
        title={t('refundOrderTitle')}
        subtitle={t('refundOrderBody')}
        // A window-wide Escape would close the panel from under its own confirmation.
        onClose={() => {
          if (!confirming) onClose();
        }}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={updating}>
              {t('cancel')}
            </Button>
            <Button type="submit" form="order-refund" disabled={!ready || updating}>
              {amount === null ? t('refund') : t('refundConfirm', { amount: shown })}
            </Button>
          </>
        }
      >
        <form
          id="order-refund"
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) setConfirming(true);
          }}
        >
          {payment ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-control border border-line p-4 text-sm">
              <dt className="text-ink-muted">{t('paymentMethod')}</dt>
              <dd className="text-ink">
                {order.paymentMethod === 'MANUAL'
                  ? t('payManual')
                  : order.paymentMethod === 'WALLET'
                    ? t('payWallet')
                    : paidByHand(order)
                      ? t('paymentCod')
                      : t('paymentCard')}
                {!paidByHand(order) && !creditOnly ? (
                  <span dir="ltr" className="ms-1 text-xs text-ink-muted">
                    ({humanize(payment.provider)})
                  </span>
                ) : null}
              </dd>
              <dt className="text-ink-muted">{t('total')}</dt>
              <dd data-numeric className="text-ink">
                {money(payment.amount, locale, payment.currency)}
              </dd>
              {payment.refundedAmount > 0 ? (
                <>
                  <dt className="text-ink-muted">{t('paymentRefunded')}</dt>
                  <dd data-numeric className="text-danger">
                    −{money(payment.refundedAmount, locale, payment.currency)}
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}

          {creditable && !creditOnly ? (
            // Where it goes: back the way it came, or to the customer's balance (ADR-081).
            <Choices
              legend={t('refundDestination')}
              name="order-refund-to"
              value={toCredit ? 'credit' : 'payment'}
              onChange={(next) => {
                setToCredit(next === 'credit');
                setTyped(null);
              }}
              options={[
                { value: 'payment', label: t('refundToPayment'), hint: byHandLabel(order, t) },
                { value: 'credit', label: t('refundToCreditLabel'), hint: t('refundToCreditHint') },
              ]}
            />
          ) : null}

          <Field
            label={t('refundAmountLabel')}
            htmlFor="order-refund-amount"
            hint={t('refundLeft', { amount: fmt(left) })}
            error={typed !== null ? amountError : undefined}
          >
            <Input
              id="order-refund-amount"
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              value={value}
              invalid={typed !== null && !check.ok}
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field>

          <Field label={t('refundWhy')} htmlFor="order-refund-reason" hint={t('reasonHint')}>
            <Textarea
              id="order-refund-reason"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>

          {byHand ? (
            // No provider holds the money: say how it went back, so the books can find it.
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('refundMethodLabel')} htmlFor="order-refund-method">
                <Select
                  id="order-refund-method"
                  className="w-full"
                  value={method}
                  onChange={(event) => setMethod(event.target.value as RefundMethod | '')}
                >
                  <option value="">{t('dispositionPick')}</option>
                  {REFUND_METHODS.map((entry) => (
                    <option key={entry} value={entry}>
                      {t(`refundMethod${entry}` as MessageKey)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('refundReference')} htmlFor="order-refund-reference" hint={t('refundReferenceHint')}>
                <Input
                  id="order-refund-reference"
                  dir="ltr"
                  maxLength={120}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
              </Field>
            </div>
          ) : null}

          {whole ? (
            <ToggleField label={t('refundRestockAll')} hint={t('refundRestockAllHint')} checked={restock} onChange={setRestock} />
          ) : null}

          {/* What will happen, in words, before the button is pressed — and again in the confirmation. */}
          {amount !== null ? <p className="text-sm text-ink">{statement}</p> : null}

          {currentError ? <Alert>{currentError}</Alert> : null}
        </form>
      </Sheet>

      <ConfirmDialog
        open={confirming}
        title={t('refundConfirmTitle', { amount: shown })}
        body={statement}
        confirmLabel={t('refundConfirm', { amount: shown })}
        cancelLabel={t('cancel')}
        busy={updating}
        onConfirm={() => void confirm()}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}

/** How the money came in, as the "back the way it came" choice says it. */
function byHandLabel(order: OrderDto, t: ReturnType<typeof useT>): string {
  if (order.paymentMethod === 'MANUAL') return t('payManual');
  return paidByHand(order) ? t('paymentCod') : t('paymentCard');
}
